import React from 'react';
import { useIntl } from 'react-intl';
import {
  ARTIST_BIO_FACTS_MAX,
  ARTIST_BIO_LONG_MAX,
  ARTIST_BIO_SHORT_MAX,
  type ArtistProfileResponse,
} from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { Panel } from '../../../components/primitives/Panel';
import { useLeaveGuard } from '../../../../smartLinks/catalog/song/page/useLeaveGuard';
import { StudioActionError } from '../../studio/StudioActionError';
import { useArtistProfile } from '../../studio/spotifyHeader/useArtistProfile';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import type { ProfileRoute } from '../useProfileRoute';
import { useCopyText } from '../useCopyText';
import { bioCounter } from './bioRules';
import { BioField } from './BioField';
import { BioLeaveDialog } from './BioLeaveDialog';
import { BioSavedLine } from './BioSavedLine';
import { BioWhereItGoes } from './BioWhereItGoes';
import { profileBioMessages as b } from './profileBioMessages';
import { useBioTool } from './useBioTool';
import { spotifyBioButtonShown } from '../spotify/spotifyBioState';
import { spotifyBioMessages as sb } from '../spotify/spotifyBioMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileBioToolProps {
  accountId: number;
  profile: ArtistProfileResponse;
  route: ProfileRoute;
}

/**
 * The bio tool (design c1, d3): "Write it for me" with the facts, the short and the long bio with
 * their counters (red over the limit, Save refused with the sentence under the field), the AI draft
 * with Keep and Discard, Copy, Save and Cancel, "Upload bio to Spotify" (the saved long bio, with
 * Auto upload access) and "Where it goes". Unsaved text guards a leave.
 */
const ProfileBioToolComponent: React.FC<ProfileBioToolProps> = ({ accountId, profile, route }) => {
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const artist = useArtistProfile(accountId);
  const bio = useBioTool(accountId, profile.bio);
  const guard = useLeaveGuard(bio.isDirty);
  const { copy, copied } = useCopyText();
  const shortOver = bioCounter(bio.texts.bioShort, ARTIST_BIO_SHORT_MAX).over;
  const longOver = bioCounter(bio.texts.bioLong, ARTIST_BIO_LONG_MAX).over;
  // The saved bio goes, so the button waits for Save.
  const canUpload = spotifyBioButtonShown(profile) && !bio.isDirty;
  const cancel = () => {
    bio.discard();
    route.close();
  };

  return (
    <div className={styles.tool} data-testid="profile-tool-bio">
      <div className={styles.toolMain}>
        <div className={styles.useIt}>
          <div className={styles.grow}>
            <b>{intl.formatMessage(b.writeTitle)}</b>
            {!narrow && <small>{intl.formatMessage(b.writeBody)}</small>}
          </div>
          <SMLButton variant="primary" disabled={bio.isDrafting} onClick={bio.writeDraft}>
            {intl.formatMessage(bio.isDrafting ? b.writing : narrow ? b.writeDraftNarrow : b.writeDraft)}
          </SMLButton>
        </div>
        <StudioActionError feedback={bio.feedback} />
        <Panel pad>
          <div className="d-flex flex-column gap-3">
            <BioField
              title={intl.formatMessage(b.factsTitle)}
              hint={intl.formatMessage(b.factsHint)}
              value={bio.facts}
              placeholder=""
              testId="bio-facts"
              onChange={(text) => bio.setFacts(Array.from(text).slice(0, ARTIST_BIO_FACTS_MAX).join(''))}
            />
            <BioField
              title={intl.formatMessage(b.shortTitle)}
              hint={intl.formatMessage(b.shortHint)}
              value={bio.texts.bioShort}
              placeholder={intl.formatMessage(b.shortPlaceholder)}
              max={ARTIST_BIO_SHORT_MAX}
              isAiDraft={bio.isAiDraft}
              error={shortOver > 0 ? intl.formatMessage(b.shortTooLong, { count: shortOver }) : null}
              testId="bio-short"
              onChange={(text) => bio.edit('bioShort', text)}
              onKeep={bio.keep}
              onDiscard={bio.discard}
              onCopy={() => copy('short', bio.texts.bioShort)}
              copied={copied === 'short'}
            />
            <BioField
              title={intl.formatMessage(b.longTitle)}
              hint={intl.formatMessage(b.longHint)}
              value={bio.texts.bioLong}
              placeholder={intl.formatMessage(b.longPlaceholder)}
              max={ARTIST_BIO_LONG_MAX}
              tall
              isAiDraft={bio.isAiDraft}
              error={longOver > 0 ? intl.formatMessage(b.longTooLong, { count: longOver }) : null}
              testId="bio-long"
              onChange={(text) => bio.edit('bioLong', text)}
              onKeep={bio.keep}
              onDiscard={bio.discard}
              onCopy={() => copy('long', bio.texts.bioLong)}
              copied={copied === 'long'}
            />
            <div className={styles.row}>
              <SMLButton
                variant="primary"
                disabled={bio.isSaving || bio.refusal !== null || !bio.isDirty}
                onClick={bio.save}
              >
                {intl.formatMessage(bio.isSaving ? b.saving : b.save)}
              </SMLButton>
              <SMLButton onClick={cancel}>{intl.formatMessage(b.cancel)}</SMLButton>
              {canUpload && (
                <SMLButton onClick={route.openBioSheet}>
                  <span className={styles.spotDot} aria-hidden="true" />
                  {intl.formatMessage(narrow ? sb.buttonShort : sb.button)}
                </SMLButton>
              )}
              <span className={styles.grow} />
              <BioSavedLine bio={profile.bio} savedNow={bio.savedNow} />
            </div>
          </div>
        </Panel>
      </div>
      <BioWhereItGoes
        artist={profile.spotify.artistName ?? artist.name ?? ''}
        canUpload={profile.spotify.bio.unavailable !== 'noAutoUploadAccess'}
      />
      <BioLeaveDialog isOpen={guard.isBlocked} onLeave={guard.proceed} onStay={guard.cancel} />
    </div>
  );
};

export const ProfileBioTool = TypedMemo(ProfileBioToolComponent);
